import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as momentImported from 'moment';
import { isAuthenticated as isAuthenticatedSelector } from '../store/selectors';
import { AuthRestore } from '../store/actions';
import { AuthState, TokenRefreshResponse } from '../typings/auth';
import { ICmxClientConfig } from '../typings/client-config';
import { State } from '../typings/state';
import { PersistenceService } from './persistence.service';
import { CmxAuth, SessionService } from '@cemex-core/angular-services-v8';

const moment = momentImported;

@Injectable()
export class AuthService {
  private isAuthenticatedValue = false;
  private apiHost = this.config.apiHost;
  private loginUrl = this.config.loginUrl;
  constructor(
    @Inject('ClientConfig') private config: ICmxClientConfig,
    private http: HttpClient,
    private store: Store<State>,
    private persistence: PersistenceService,
    private sessionService: SessionService,
  ) {
    this.store.pipe(select(isAuthenticatedSelector)).subscribe((value) => (this.isAuthenticatedValue = value));
  }

  isAuthenticated(): boolean {
    // Check if AuthState is already restored from persistence
    if (this.isAuthenticatedValue) {
      return true;
    }
    // Try to restore AuthState from persistence
    const authState = this.persistence.getAuthState();
    if (!authState || Object.keys(authState).length === 0) {
      this.redirectToLogin();
      return false;
    }
    if (this.isExpired(authState.expiresIn)) {
      this.redirectToLogin();
      return false;
    }
    // restore state and refresh token cycle
    this.setRefreshTimeout(authState);
    this.store.dispatch(new AuthRestore(authState));
    return true;
  }

  async login(email: string, pass: string, redirect?: string) {
    const response = await this.loginApi(email, pass);
    this.sessionService.saveProcessDataFromLogin(response);
    const authState: AuthState = {
      ...response,
      expiresIn: this.calculateExpiration(response.oauth2.expires_in),
    };
    this.setRefreshTimeout(authState);
    redirect ? window.location.assign(redirect) : window.location.assign('/');
    return authState;
  }

  logout() {
    this.persistence.removeAuthState();
    window.location.assign(this.loginUrl);
  }

  private getActiveRoute(): string {
    return window.location.pathname + window.location.search;
  }

  private async redirectToLogin() {
    const url = this.getActiveRoute();
    const regLogin = new RegExp(this.loginUrl + '.*');
    const regLoginWithRedirect = new RegExp(this.loginUrl + '?returnUrl=(.*)');
    // no redirect, already login
    if (regLogin.exec(url) || regLoginWithRedirect.exec(url)) {
      return;
    }
    // redirect, no param
    if (url === '' || url === '/') {
      // this.router.navigate([this.loginUrl])
      window.location.assign(this.loginUrl);
      return;
    }
    // redirect, with param
    // this.router.navigate([this.loginUrl], { queryParams: { returnUrl: url } })
    window.location.assign(`${this.loginUrl}?returnUrl=${encodeURIComponent(url)}`);
  }

  private isExpired(expiresIn: Date) {
    return moment().isAfter(moment(expiresIn));
  }

  private calculateExpiration(timeToLive: number) {
    return moment()
      .add(timeToLive - 300, 'seconds')
      .toDate();
  }

  private async refreshToken(authState: AuthState) {
    try {
      // Refresh Token and Update Local Storage
      const response = await this.refreshTokenApi(authState.oauth2.refresh_token);
      authState.expiresIn = this.calculateExpiration(response.expires_in);
      authState.oauth2.expires_in = response.expires_in;
      authState.oauth2.access_token = response.access_token;
      authState.oauth2.refresh_token = response.refresh_token;
      this.setRefreshTimeout(authState);
      this.sessionService.saveProcessDataFromLogin(authState);
      this.store.dispatch(new AuthRestore(authState));
    } catch (error) {
      this.logout();
    }
  }

  private setRefreshTimeout(authState: AuthState) {
    const timeToLive = moment(authState.expiresIn).diff(new Date());
    setTimeout(() => this.refreshToken(authState), timeToLive);
  }

  /**
   * Api Calls. TODO: Should be from api module. (API module not yet in Angular7)
   */

  private loginApi(email: string, pass: string) {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('scope', 'security')
      .set('username', email)
      .set('password', pass);
    return this.http
      .post<CmxAuth>(`${this.apiHost}/v2/secm/oam/oauth2/token`, body.toString(), {
        headers: new HttpHeaders()
          .append('Content-Type', 'application/x-www-form-urlencoded')
          .append('App-Code', 'DCMWebTool_App'),
      })
      .toPromise();
  }

  private refreshTokenApi(refreshToken: string) {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('scope', 'security')
      .set('refresh_token', refreshToken);
    return this.http
      .post<TokenRefreshResponse>(`${this.apiHost}/v2/secm/oam/oauth2/token`, body.toString(), {
        headers: new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded'),
      })
      .toPromise();
  }
}
